<template>
  <div class="self-assess-page">
    <!-- Hero -->
    <div class="hero">
      <div class="hero-inner">
        <p class="eyebrow">Self Assessment</p>
        <h1>Am I Ready for Pointe?</h1>
        <p class="subtitle">Browse all of our en pointe criteria below. Use this to understand what we look for — and to guide your own preparation.</p>
      </div>
    </div>

    <div class="page-inner">

      <!-- Category filter -->
      <div class="filter-row">
        <button
          v-for="cat in allCategories"
          :key="cat"
          class="cat-filter-btn"
          :class="{ active: selectedCategory === cat }"
          @click="selectedCategory = cat"
        >{{ cat }}</button>
      </div>

      <div v-if="loading" class="loading-wrap">
        <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
      </div>

      <div v-else-if="filteredCriteria.length === 0" class="empty-state">
        <p>No criteria available yet.</p>
      </div>

      <div v-else class="criteria-grid">
        <div v-for="criterion in filteredCriteria" :key="criterion.id" class="criterion-card">
          <div class="criterion-header">
            <h3>{{ criterion.title }}</h3>
            <span v-if="criterion.category" class="cat-chip">{{ criterion.category }}</span>
          </div>
          <p v-if="criterion.description" class="criterion-desc">{{ criterion.description }}</p>
          <div v-if="criterion.resourceLinks && criterion.resourceLinks.length" class="resource-links">
            <p class="resource-label">Helpful Resources</p>
            <div class="link-list">
              <a
                v-for="link in criterion.resourceLinks"
                :key="link.url"
                :href="link.url"
                target="_blank"
                class="resource-link"
              >{{ link.label }} →</a>
            </div>
          </div>
        </div>
      </div>

      <!-- Upsell section -->
      <div class="upsell-section">
        <div class="upsell-inner">
          <div class="upsell-col">
            <h2>Ready to get assessed?</h2>
            <p>
              The Foot 360 Consultation gives you a full personalized assessment against every
              criterion on this page — plus a custom practice plan and 2 free re-application reviews.
            </p>
            <a href="mailto:hello@brocheballet.com?subject=Foot%20360%20Consultation%20Inquiry" class="btn-dark">
              Book a Foot 360 Consultation →
            </a>
          </div>
          <div class="upsell-col">
            <h2>Applications are open?</h2>
            <div v-if="activeCohort">
              <p>The <strong>{{ activeCohort.name }}</strong> cohort is accepting applications now.</p>
              <router-link to="/pointe/apply" class="btn-outline">Apply Now →</router-link>
            </div>
            <div v-else>
              <p>We open cohort applications twice a year. Join the waitlist to be notified first.</p>
              <div class="convertkit-embed" ref="convertkitEmbed"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  data () {
    return {
      criteria: [],
      loading: true,
      selectedCategory: 'All',
      activeCohort: null
    }
  },
  computed: {
    allCategories () {
      const cats = ['All', ...new Set(this.criteria.map(c => c.category).filter(Boolean))]
      return cats
    },
    filteredCriteria () {
      if (this.selectedCategory === 'All') return this.criteria
      return this.criteria.filter(c => c.category === this.selectedCategory)
    }
  },
  mounted () {
    this.$nextTick(() => {
      const container = this.$refs.convertkitEmbed
      if (!container) return
      const script = document.createElement('script')
      script.async = true
      script.setAttribute('data-uid', '0166d3e2a8')
      script.src = 'https://broche-ballet.kit.com/0166d3e2a8/index.js'
      container.appendChild(script)
    })
  },
  async created () {
    this.$store.commit('setLayout', 'pointe-layout')
    const [criteriaSnap, cohortSnap] = await Promise.all([
      db.collection('pointe-criteria').where('active', '==', true).orderBy('order').get(),
      db.collection('pointe-cohorts').where('status', '==', 'open').limit(1).get()
    ])
    this.criteria = criteriaSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.activeCohort = cohortSnap.empty ? null : { id: cohortSnap.docs[0].id, ...cohortSnap.docs[0].data() }
    this.loading = false
  }
}
</script>

<style scoped>
.self-assess-page {
  font-family: 'Fanwood Text', Georgia, serif;
}

/* Hero */
.hero {
  background: #1a1a1a;
  color: #e8e5e1;
  padding: 60px 24px;
}

.hero-inner {
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
}

.eyebrow {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .12em;
  color: #888;
  margin: 0 0 10px;
}

.hero h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 42px;
  font-weight: 700;
  color: #e8e5e1;
  margin: 0 0 16px;
}

.subtitle { font-size: 17px; color: #bbb; line-height: 1.7; margin: 0; }

/* Page inner */
.page-inner { max-width: 1100px; margin: 0 auto; padding: 40px 24px; }

/* Category filter */
.filter-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 32px; }

.cat-filter-btn {
  background: #fff;
  border: 1px solid #e8e5e1;
  padding: 8px 18px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  cursor: pointer;
  border-radius: 20px;
  color: #1a1a1a;
  transition: all 0.15s;
}

.cat-filter-btn:hover { border-color: #1a1a1a; }
.cat-filter-btn.active { background: #1a1a1a; color: #fff; border-color: #1a1a1a; }

.loading-wrap { padding: 60px; text-align: center; }
.empty-state { padding: 60px; text-align: center; color: #888; }

/* Criteria grid */
.criteria-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 60px;
}

.criterion-card {
  background: #fff;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 20px;
}

.criterion-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.criterion-header h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 17px;
  color: #1a1a1a;
  margin: 0;
  flex: 1;
  line-height: 1.3;
}

.cat-chip {
  background: #f5f4f2;
  color: #888;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .05em;
  white-space: nowrap;
  flex-shrink: 0;
}

.criterion-desc { font-size: 14px; color: #666; line-height: 1.6; margin: 0 0 12px; }

.resource-links { margin-top: 12px; }

.resource-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #888;
  margin: 0 0 6px;
}

.link-list { display: flex; gap: 6px; flex-wrap: wrap; }

.resource-link {
  font-size: 13px;
  color: #1a1a1a;
  text-decoration: none;
  background: #faf9f7;
  border: 1px solid #e8e5e1;
  padding: 4px 12px;
  border-radius: 20px;
  transition: background 0.15s;
}

.resource-link:hover { background: #e8e5e1; }

/* Upsell */
.upsell-section {
  background: #1a1a1a;
  border-radius: 4px;
  padding: 48px;
  margin-bottom: 20px;
}

.upsell-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
}

.upsell-col h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 22px;
  color: #e8e5e1;
  margin: 0 0 12px;
}

.upsell-col p { font-size: 15px; color: #bbb; line-height: 1.7; margin: 0 0 20px; }
.upsell-col p strong { color: #e8e5e1; }

.btn-dark {
  display: inline-block;
  background: #e8e5e1;
  color: #1a1a1a;
  padding: 12px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  text-decoration: none;
  border-radius: 2px;
}

.btn-dark:hover { background: #fff; }

.btn-outline {
  display: inline-block;
  border: 1px solid #e8e5e1;
  color: #e8e5e1;
  padding: 11px 22px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  text-decoration: none;
  border-radius: 2px;
}

.btn-outline:hover { background: rgba(255,255,255,.1); }

.convertkit-embed { margin-top: 12px; }

@media (max-width: 768px) {
  .upsell-inner { grid-template-columns: 1fr; gap: 32px; }
  .upsell-section { padding: 28px 20px; }
  .hero h1 { font-size: 30px; }
}
</style>
